import {
  CheckboxField,
  Form,
  Loading,
  SelectField,
  SubmitButton,
  TextAreaInputField,
} from '@/components';
import { PaymentEdgeFormSchema } from '@/features/project/projects/_models/payment-edge';
import { usePaymentGroupOptions } from '@/features/project/projects/detail/payment/_hooks/payment-group';
import { useForm, useId } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof PaymentEdgeFormSchema>;

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const PaymentEdgeForm: FC<Props> = ({ initialValues, onSubmit }) => {
  const { t } = useTranslation();
  const { id } = useId();
  const {
    handleSubmit,
    resetField,
    watch,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: {
      isConnectedToSourceRoot: false,
      ...initialValues,
    },
    resolver: zodResolver(
      PaymentEdgeFormSchema.refine(
        (data) => {
          const isValid = data.isConnectedToSourceRoot || data.sourceNodeId;
          return isValid;
        },
        {
          path: ['sourceNodeId'],
          message: t('validation.required'),
        },
      ),
    ),
  });
  const isConnectedToSourceRoot = watch('isConnectedToSourceRoot');
  const sourceNodeId = watch('sourceNodeId');
  const paymentGroupOptions = usePaymentGroupOptions(id);

  if (paymentGroupOptions.isPending || !paymentGroupOptions.data) {
    return <Loading />;
  }

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <CheckboxField
        name="isConnectedToSourceRoot"
        text={t('is-connected-to-source-root')}
        control={control}
        errors={errors}
        shouldCoexistOnChange
        onChange={(checked) => {
          if (checked) {
            resetField('sourceNodeId', { defaultValue: null as any });
          }
        }}
      />

      <SelectField
        name="sourceNodeId"
        label={t('source-payment-node')}
        control={control}
        options={paymentGroupOptions.data}
        errors={errors}
        disabled={isConnectedToSourceRoot}
        required={!isConnectedToSourceRoot}
        shouldCoexistOnChange
        onChange={() => {
          resetField('targetNodeId', { defaultValue: null as any });
        }}
      />

      <SelectField
        name="targetNodeId"
        label={t('target-payment-node')}
        control={control}
        options={paymentGroupOptions.data.filter(
          (d) => d.value !== sourceNodeId,
        )}
        errors={errors}
        required
      />

      <TextAreaInputField
        name="label"
        label={t('payment-edge-label')}
        control={control}
        errors={errors}
        rows={3}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
